import {
	HttpClient,
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
	HttpStatusCode,
} from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { Observable, Subscription, throwError } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

import { ApplicationType, SystemMessage, SystemMessageType } from '../openapi/models';
import { LoggerService } from '../services/logger.service';

import { SnackBarService } from 'projects/design-system/components/snackbar/snackbar.service';
import { revokeAuthentication401 } from 'shared/auth/store/auth.actions';
import { SnackBarConfig } from 'shared/configs/snackbar.config';
import { CsrfTokenCookieName } from 'shared/openapi/models/csrf-token-cookie-name';

@Injectable({ providedIn: 'root' })
export class HttpErrorInterceptor implements HttpInterceptor, OnDestroy {
	private subscriptions: Subscription = new Subscription();

	constructor(
		private snackBarService: SnackBarService,
		private translate: TranslateService,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		private store: Store<{ auth: any }>,
		private router: Router,
		private logger: LoggerService,
		private httpClient: HttpClient,
		private cookieService: CookieService
	) {}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		return next.handle(request).pipe(
			catchError((error: HttpErrorResponse) => {
				const isErrorMessageAString: boolean = typeof error.error === 'string';
				const isErrorBlobAndJson: boolean =
					error.error instanceof Blob && error.error.type === 'application/json';
				let exceptionTraceId: string = '';
				let systemMessages: SystemMessage[] = [];
				if (isErrorMessageAString) {
					systemMessages = JSON.parse(error.error).systemMessages;
					exceptionTraceId = JSON.parse(error.error).exceptionTraceId;
				} else if (isErrorBlobAndJson) {
					const reader = new FileReader();
					reader.onload = (e: ProgressEvent<FileReader>) => {
						systemMessages = JSON.parse(String(e?.target?.result)).systemMessages;
						this.snackBarService.error({
							message: systemMessages[0].message,
							config: SnackBarConfig,
						});
					};
					reader.readAsText(error.error);
					exceptionTraceId = JSON.parse(error.error).exceptionTraceId;
				} else if (null != error.error) {
					systemMessages = error.error.systemMessages;
					exceptionTraceId = error.error.systemMessages;
				} else {
					systemMessages = [];
				}
				this.logger.log(
					this.constructor.name,
					this.intercept.name + ' Error: ' + error?.message,
					exceptionTraceId
				);
				switch (error.status) {
					case HttpStatusCode.GatewayTimeout: {
						this.snackBarService.error({
							message: this.translate.instant('common.errors.http.504'),
							config: SnackBarConfig,
						});
						break;
					}
					case HttpStatusCode.InternalServerError: {
						this.snackBarService.error({
							message: this.translate.instant('common.errors.http.500'),
							config: SnackBarConfig,
						});
						break;
					}
					case HttpStatusCode.NotFound: {
						this.snackBarService.error({
							message: this.translate.instant('common.errors.http.404'),
							config: SnackBarConfig,
						});
						// TODO: redirect to 404 page
						break;
					}
					case HttpStatusCode.Forbidden: {
						this.snackBarService.error({
							message: this.translate.instant('common.errors.http.403'),
							config: SnackBarConfig,
						});
						break;
					}
					case HttpStatusCode.Unauthorized: {
						const actualCSRFTokenName = this.getCSRFTokenNameByApplicationType(
							request.headers.get('X-APP-TYPE')
						);
						if (request.url.startsWith('/public') && !this.cookieService.check(actualCSRFTokenName)) {
							return this.httpClient.request(request);
						} else {
							this.subscriptions.add(
								this.store
									.select('auth')
									.pipe(take(1))
									.subscribe(() => {
										this.store.dispatch(revokeAuthentication401());
									})
							);
							this.router.navigate(['/login']);
							this.snackBarService.error({
								message: this.translate.instant('common.errors.http.401'),
								config: SnackBarConfig,
							});
							break;
						}
					}
					case HttpStatusCode.BadRequest: {
						if (systemMessages) {
							const messages: Set<string> = new Set(
								systemMessages
									.filter((msg) => msg.type === SystemMessageType.Request)
									.map((msg) => msg.message)
							);
							const message = Array.from(messages).join('\n');
							if (message) {
								this.snackBarService.error({
									message: this.translate.instant(message),
									config: SnackBarConfig,
								});
							}
						} else {
							this.snackBarService.error({
								message: this.translate.instant('common.errors.http.400'),
								config: SnackBarConfig,
							});
						}
						break;
					}
				}
				if (isErrorMessageAString) {
					return throwError(
						() =>
							new HttpErrorResponse({
								error: { ...JSON.parse(error.error) },
								headers: error.headers,
								status: error.status,
								statusText: error.statusText,
								url: error.url || undefined,
							})
					);
				} else {
					return throwError(() => error);
				}
			})
		);
	}

	getCSRFTokenNameByApplicationType(applicationType: string | null): string {
		if (ApplicationType.Admin === applicationType) {
			return CsrfTokenCookieName.AdminCsrfToken;
		} else if (ApplicationType.Partner === applicationType) {
			return CsrfTokenCookieName.PartnerCsrfToken;
		} else if (ApplicationType.Client === applicationType) {
			return CsrfTokenCookieName.ClientCsrfToken;
		} else {
			return '';
		}
	}
}
